import { getRouterSelectors } from '@ngrx/router-store';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { OrbiFeatureState, orbiFeatureKey } from './orbi-feature.reducer';
import { selectAuthLoggedIn } from '../auth/auth.selectors';
import { selectPrimaryAccountHolder } from '../patient/patient.selectors';

/**
 * The LexisNexis identity-quiz status that means the patient proved who they
 * are. Compared as a string literal — the same idiom the results-eligibility
 * gate uses (`dashboard-results-auth-verification.component.ts`) — rather than
 * via `Patient.authenticationQuizPassed`, because state can be rehydrated into
 * a plain object and a class getter would not survive that.
 */
const IDENTITY_VERIFIED_QUIZ_STATUS = 'passed';

/**
 * The login HANDOFF routes: screens whose only job is to hand the patient on to
 * another screen. Every one of them is a `PublicLayoutComponent` child, which is
 * the shell that hosts the agent for the signed-in pages it also serves — so
 * without this list the entry bar draws over the "Establishing Secure
 * Connection" spinner (QA 08/25).
 *
 * `/callback`, `/mobile-callback` and `/ui/transition` are all
 * `TransitionComponent`; `/sso/entry` is the SSO landing that redirects into the
 * portal. `login/**` is deliberately absent: it renders no layout at all, so
 * there is no agent there to suppress.
 */
const ORBI_HANDOFF_ROUTES = [
  '/callback',
  '/mobile-callback',
  '/ui/transition',
  '/sso/entry',
];

const ORBI_EXCLUDED_ROUTES = ['/portal/ondemand'];

const routePath = (url: string | undefined): string =>
  (url ?? '').split(/[?#]/)[0].replace(/\/+$/, '');

/**
 * The current url, off the `@ngrx/router-store` slice.
 *
 * Built here from `getRouterSelectors()` rather than reused from
 * `@patient-ui/shared-ui/store`'s `routerQuery`, which is the same thing: the Nx
 * boundary rules forbid it, since a `type:store` lib may only depend on
 * `type:models`/`utils`/`constants`/`enums`. Taking the default with no argument
 * is what makes the two identical anyway — it selects the `'router'` feature
 * (`DEFAULT_ROUTER_FEATURENAME`), which is the key `app.module.ts` registers
 * `routerReducer` under.
 */
const { selectUrl: selectRouterUrl } = getRouterSelectors();

export const selectOrbiFeatureState =
  createFeatureSelector<OrbiFeatureState>(orbiFeatureKey);

/**
 * The Orbi Split flag, on its own. Kept as the pure flag value — the two gates
 * (flag and identity) are evaluated independently (PCSA-13452) so a failure in
 * either one is diagnosable. Prefer `selectOrbiAgentAvailable` in UI/effects.
 */
export const selectOrbiAgentEnabled = createSelector(
  selectOrbiFeatureState,
  (state: OrbiFeatureState) => state.orbiAgentEnabled
);

/**
 * Whether the signed-in patient has passed identity verification (PCSA-13452).
 *
 * `authenticationQuizStatus` arrives on the logged-in patient from the login
 * response (`/protected/patients/current`) and lands in the `patient` slice, so
 * reading it here IS "read it on login" — with the bonus that the gate also
 * re-evaluates if the patient completes verification mid-session, which can
 * only ever move hidden -> shown.
 *
 * Fail-closed: any other status ('pending', 'attempts_exhausted', …), an empty
 * string, or no patient loaded yet all resolve to false, so the agent is never
 * exposed to an unverified patient while data is in flight.
 */
export const selectOrbiIdentityVerified = createSelector(
  selectPrimaryAccountHolder,
  (patient) =>
    patient?.authenticationQuizStatus === IDENTITY_VERIFIED_QUIZ_STATUS
);

/**
 * Whether a patient is signed in at all (QA 08/21: "why am I seeing the agent on
 * the home screen when I'm not logged in?").
 *
 * The two gates above both SURVIVE a sign-out, so neither is an answer to "is
 * anyone logged in":
 *   - the `patient` slice is never reset on logout, so `authenticationQuizStatus`
 *     stays 'passed' for the patient who just left, and
 *   - logout redirects with `router.navigate`, not a page load, so nothing else
 *     clears the store either.
 * That left the signed-out landing page — a `PublicLayoutComponent` route, and
 * that shell hosts the entry point for the pages the agent links into — showing
 * an "Ask a question" bar to an anonymous visitor.
 *
 * `auth.loggedIn` is the one piece of state that tracks the SESSION rather than
 * the patient record, and it is cleared by `logOut` itself.
 */
export const selectOrbiSignedIn = selectAuthLoggedIn;

/**
 * Whether the patient is currently on a login handoff screen (QA 08/25: the
 * agent appeared on the load screen after signing in, while it read
 * "Establishing Secure Connection").
 *
 * The three gates above are all TRUE by the time that screen is on display, and
 * correctly so — `/callback` is where the token is exchanged and
 * `/protected/patients/current` lands, so the moment the patient slice arrives
 * the session is real and the identity is verified. What none of them can say is
 * that the patient has not ARRIVED anywhere yet: the shell is still mid-handoff,
 * one `router.navigate` away from the dashboard. So the bar drew itself over the
 * spinner for the fraction of a second between the login response and the
 * redirect — offering to answer questions about a page that does not exist, on a
 * screen the patient cannot interact with.
 *
 * Route-based because the condition IS "which screen is this", and the router
 * slice is the only state that knows. Matched on the path with the query string
 * stripped — `/callback` always carries Okta's `?code=&state=` — and on the whole
 * path rather than a prefix, so a real portal route that merely starts with one
 * of these words is unaffected.
 */
export const selectOrbiOnHandoffRoute = createSelector(selectRouterUrl, (url) =>
  ORBI_HANDOFF_ROUTES.includes(routePath(url))
);

export const selectOrbiOnExcludedRoute = createSelector(
  selectRouterUrl,
  (url) => {
    const path = routePath(url);
    return ORBI_EXCLUDED_ROUTES.some(
      (excluded) => path === excluded || path.startsWith(`${excluded}/`)
    );
  }
);

/**
 * The single source of truth for whether the web AI agent is available.
 * Consumed by the entry point (PCSA-13157), the chat panel (PCSA-13158), and
 * the gateway-call gate in OrbiEffects.
 *
 * ALL FIVE conditions must hold: the Split flag is on, the patient is
 * identity-verified (PCSA-13452), there IS a signed-in session (QA 08/21), the
 * patient is not mid-handoff on a login interstitial (QA 08/25) and the page is
 * not one the agent is excluded from.
 * When it is false there is no agent UI anywhere — the entry point, its input
 * field, and the panel are all removed from the DOM (the entry point is
 * `position: fixed`, so removing it also leaves no gap in the page flow) and
 * OrbiEffects makes no call to the agent gateway.
 */
export const selectOrbiAgentAvailable = createSelector(
  selectOrbiAgentEnabled,
  selectOrbiIdentityVerified,
  selectOrbiSignedIn,
  selectOrbiOnHandoffRoute,
  selectOrbiOnExcludedRoute,
  (flagEnabled, identityVerified, signedIn, onHandoffRoute, onExcludedRoute) =>
    flagEnabled &&
    identityVerified &&
    signedIn &&
    !onHandoffRoute &&
    !onExcludedRoute
);

export const orbiFeatureQuery = {
  selectOrbiFeatureState,
  selectOrbiAgentEnabled,
  selectOrbiIdentityVerified,
  selectOrbiSignedIn,
  selectOrbiOnHandoffRoute,
  selectOrbiOnExcludedRoute,
  selectOrbiAgentAvailable,
};
